import React, { useEffect, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
    AccountCircle as AccountIcon,
    Business as BusinessIcon,
    Cancel as CancelIcon,
    Close as CloseIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    Save as SaveIcon,
    SwapHoriz as MoveIcon,
} from '@mui/icons-material';
import { updateUser } from '../services/userManagementService';
import { usePermissions } from '../../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../../utils/permissions';
import MoveCompanyDialog from './MoveCompanyDialog';
import EditBusinessDialog from './EditBusinessDialog';

const FIELD_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
    },
};

const SECTION_SX = {
    p: 2.5,
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
};

const formatBusinessAddress = ({
    streetAddress = '',
    city = '',
    province = '',
    postalCode = '',
    country = '',
}) => [streetAddress, city, province, postalCode, country].filter(Boolean).join(', ');

const businessFromDto = (business) => ({
    businessId: business.businessId,
    businessName: business.businessName,
    businessStreetAddress: business.streetAddress,
    businessCity: business.city,
    businessProvince: business.province,
    businessPostalCode: business.postalCode,
    businessCountry: business.country,
    businessAddress: formatBusinessAddress({
        streetAddress: business.streetAddress,
        city: business.city,
        province: business.province,
        postalCode: business.postalCode,
        country: business.country,
    }),
    businessPhone: business.phoneNumber,
    businessEmail: business.email,
});

const UserDetailsModal = ({ open, onClose, user, onUserUpdated }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { canBusiness } = usePermissions();
    const canEditUser = canBusiness(PERMISSIONS.USER_WRITE);
    const canEditBusiness = canBusiness(PERMISSIONS.BUSINESS_WRITE);

    const [currentUserData, setCurrentUserData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [userFormData, setUserFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        accountStatus: '',
    });

    const [moveCompanyOpen, setMoveCompanyOpen] = useState(false);
    const [editBusinessOpen, setEditBusinessOpen] = useState(false);

    useEffect(() => {
        if (!user) return;

        setCurrentUserData(user);
        setUserFormData({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phoneNumber: user.phoneNumber || '',
            accountStatus: user.accountStatus || '',
        });

        if (open) {
            setIsEditing(false);
            setError('');
            setSuccess('');
            setMoveCompanyOpen(false);
            setEditBusinessOpen(false);
        }
    }, [user, open]);

    const userData = currentUserData;
    const hasBusinessData = Boolean(userData?.businessName?.trim());
    const isInternal = Boolean(userData?.isInternalUser);
    const canMoveCompany =
        canEditUser && !isInternal && hasBusinessData && Boolean(userData?.businessMembershipId);
    const canEditCompanyDetails = canEditBusiness && !isInternal && hasBusinessData;

    if (!user || !userData) return null;

    const getInitials = (firstName, lastName) =>
        `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();

    const formatRoleDisplay = (role) => {
        if (!role) return 'No Role';
        return role
            .replace(/[_]/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (letter) => letter.toUpperCase());
    };

    const formatPhoneNumber = (phone) => {
        if (!phone) return 'Not provided';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        }
        if (cleaned.length === 11 && cleaned[0] === '1') {
            return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
        }
        if (cleaned.length > 6) return cleaned.replace(/(\d{3})(\d{3})(\d+)/, '$1 $2 $3');
        return phone;
    };

    const cleanPhoneNumber = (phoneNumber) => (phoneNumber || '').replace(/[^0-9]/g, '');

    const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();

    const handleUserFormChange = (field, value) => {
        setUserFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleDigitsOnlyChange = (field, value, setter) => {
        if (!/^[0-9]*$/.test(value)) return;
        setter(field, value);
    };

    const handleSaveUser = async () => {
        setError('');
        setSuccess('');
        setLoading(true);

        const userPayload = {
            userId: userData.userId,
            firstName: userFormData.firstName,
            lastName: userFormData.lastName,
            email: userFormData.email,
            phoneNumber: cleanPhoneNumber(userFormData.phoneNumber),
            accountStatus: userFormData.accountStatus,
        };

        try {
            await updateUser(userData.userId, userPayload);
            setCurrentUserData((prev) => ({
                ...prev,
                firstName: userFormData.firstName,
                lastName: userFormData.lastName,
                email: userFormData.email,
                phoneNumber: userFormData.phoneNumber,
                accountStatus: userFormData.accountStatus,
            }));
            setSuccess('User information updated successfully.');
            setIsEditing(false);
            onUserUpdated?.();
        } catch (err) {
            if (err.response?.status === 409) {
                setError(
                    err.response.data || 'Email or phone number already exists in the system.'
                );
            } else if (err.response?.data && typeof err.response.data === 'string') {
                setError(err.response.data);
            } else {
                setError('Failed to update user information. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleMoveComplete = ({ result, message }) => {
        setCurrentUserData((prev) => ({
            ...prev,
            ...businessFromDto(result.business),
            businessMembershipId: result.businessMembershipId || prev.businessMembershipId,
        }));
        setSuccess(message);
        setMoveCompanyOpen(false);
        onUserUpdated?.();
    };

    const handleEditBusinessComplete = (updatedBusiness) => {
        setCurrentUserData((prev) => ({
            ...prev,
            ...businessFromDto(updatedBusiness),
        }));
        setSuccess('Company details updated successfully.');
        setEditBusinessOpen(false);
        onUserUpdated?.();
    };

    const handleCancel = () => {
        setUserFormData({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phoneNumber: user.phoneNumber || '',
            accountStatus: user.accountStatus || '',
        });
        setIsEditing(false);
        setError('');
        setSuccess('');
    };

    const handleClose = () => {
        if (isEditing) handleCancel();
        onClose();
    };

    const renderLabel = (label, value) => (
        <Box>
            <Typography
                variant="body2"
                sx={{
                    color: '#64748b',
                    fontWeight: 500,
                    mb: 0.5,
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    letterSpacing: '0.5px',
                }}
            >
                {label}
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: '#0f172a',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    overflowWrap: 'anywhere',
                }}
            >
                {value || 'Not provided'}
            </Typography>
        </Box>
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : '16px',
                        maxWidth: isMobile ? '100%' : '700px',
                        m: isMobile ? 0 : 2,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 2,
                        px: { xs: 2, sm: 3 },
                        pt: { xs: 2, sm: 3 },
                        borderBottom: '1px solid #f1f5f9',
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 600,
                            color: '#0f172a',
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        }}
                    >
                        {isEditing ? 'Edit User' : 'User Details'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {!isEditing && canEditUser && (
                            <IconButton
                                onClick={() => setIsEditing(true)}
                                sx={{
                                    color: '#1652f0',
                                    backgroundColor: 'rgba(22, 82, 240, 0.08)',
                                    '&:hover': { backgroundColor: 'rgba(22, 82, 240, 0.12)' },
                                }}
                            >
                                <EditIcon />
                            </IconButton>
                        )}
                        <IconButton
                            onClick={handleClose}
                            sx={{
                                color: '#64748b',
                                '&:hover': { backgroundColor: 'rgba(100, 116, 139, 0.08)' },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
                    <Stack spacing={{ xs: 2, sm: 2.5 }}>
                        {error && <Alert severity="error">{error}</Alert>}
                        {success && <Alert severity="success">{success}</Alert>}

                        <Card
                            sx={{
                                backgroundColor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                boxShadow: 'none',
                            }}
                        >
                            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        textAlign: { xs: 'center', sm: 'left' },
                                        gap: { xs: 2, sm: 3 },
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            width: { xs: 56, sm: 64 },
                                            height: { xs: 56, sm: 64 },
                                            backgroundColor: '#1652f0',
                                            color: 'white',
                                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                            fontWeight: 600,
                                        }}
                                    >
                                        {getInitials(
                                            isEditing ? userFormData.firstName : userData.firstName,
                                            isEditing ? userFormData.lastName : userData.lastName
                                        )}
                                    </Avatar>

                                    <Box sx={{ flex: 1, width: '100%' }}>
                                        {isEditing ? (
                                            <Stack spacing={2}>
                                                <Grid2 container spacing={2}>
                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                        <TextField
                                                            fullWidth
                                                            label="First Name"
                                                            value={userFormData.firstName}
                                                            onChange={(e) =>
                                                                handleUserFormChange(
                                                                    'firstName',
                                                                    e.target.value
                                                                )
                                                            }
                                                            size="small"
                                                            sx={FIELD_SX}
                                                        />
                                                    </Grid2>
                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                        <TextField
                                                            fullWidth
                                                            label="Last Name"
                                                            value={userFormData.lastName}
                                                            onChange={(e) =>
                                                                handleUserFormChange(
                                                                    'lastName',
                                                                    e.target.value
                                                                )
                                                            }
                                                            size="small"
                                                            sx={FIELD_SX}
                                                        />
                                                    </Grid2>
                                                </Grid2>
                                                <Grid2 container spacing={2}>
                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                        <TextField
                                                            fullWidth
                                                            select
                                                            label="Account Status"
                                                            value={userFormData.accountStatus}
                                                            onChange={(e) =>
                                                                handleUserFormChange(
                                                                    'accountStatus',
                                                                    e.target.value
                                                                )
                                                            }
                                                            size="small"
                                                            sx={FIELD_SX}
                                                        >
                                                            <MenuItem value="ACTIVE">
                                                                Active
                                                            </MenuItem>
                                                            <MenuItem value="INACTIVE">
                                                                Inactive
                                                            </MenuItem>
                                                        </TextField>
                                                    </Grid2>
                                                </Grid2>
                                            </Stack>
                                        ) : (
                                            <>
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: '#0f172a',
                                                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                                        mb: 1,
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    {fullName || 'Unnamed User'}
                                                </Typography>
                                                <Stack
                                                    direction={{ xs: 'column', sm: 'row' }}
                                                    spacing={1}
                                                    alignItems={{ xs: 'center', sm: 'flex-start' }}
                                                >
                                                    <Chip
                                                        label={formatRoleDisplay(userData.role)}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: isInternal
                                                                ? '#dbeafe'
                                                                : '#ecfdf5',
                                                            color: isInternal
                                                                ? '#2563eb'
                                                                : '#059669',
                                                            fontWeight: 500,
                                                            borderRadius: '8px',
                                                        }}
                                                    />
                                                    <Chip
                                                        label={userData.accountStatus}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor:
                                                                userData.accountStatus === 'ACTIVE'
                                                                    ? '#dcfce7'
                                                                    : '#fee2e2',
                                                            color:
                                                                userData.accountStatus === 'ACTIVE'
                                                                    ? '#16a34a'
                                                                    : '#dc2626',
                                                            fontWeight: 500,
                                                            borderRadius: '8px',
                                                        }}
                                                    />
                                                </Stack>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        <Box sx={SECTION_SX}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                                <EmailIcon
                                    sx={{ color: '#1652f0', fontSize: '1.25rem', mr: 1.5 }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{ color: '#0f172a', fontWeight: 600, fontSize: '1rem' }}
                                >
                                    Contact Information
                                </Typography>
                            </Box>
                            <Grid2 container spacing={3}>
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    {isEditing ? (
                                        <Stack spacing={1}>
                                            <TextField
                                                fullWidth
                                                type="email"
                                                label="Email Address"
                                                value={userFormData.email}
                                                onChange={(e) =>
                                                    handleUserFormChange('email', e.target.value)
                                                }
                                                size="small"
                                                sx={FIELD_SX}
                                            />
                                            {userFormData.email !== userData.email && (
                                                <Alert severity="warning">
                                                    Changing this email updates the login username.
                                                </Alert>
                                            )}
                                        </Stack>
                                    ) : (
                                        renderLabel('Email Address', userData.email)
                                    )}
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    {isEditing ? (
                                        <TextField
                                            fullWidth
                                            label="Phone Number"
                                            value={userFormData.phoneNumber}
                                            onChange={(e) =>
                                                handleDigitsOnlyChange(
                                                    'phoneNumber',
                                                    e.target.value,
                                                    handleUserFormChange
                                                )
                                            }
                                            placeholder="1234567890"
                                            size="small"
                                            sx={FIELD_SX}
                                        />
                                    ) : (
                                        renderLabel(
                                            'Phone Number',
                                            formatPhoneNumber(userData.phoneNumber)
                                        )
                                    )}
                                </Grid2>
                            </Grid2>
                        </Box>

                        {hasBusinessData && (
                            <Box sx={SECTION_SX}>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    alignItems={{ xs: 'stretch', sm: 'center' }}
                                    justifyContent="space-between"
                                    sx={{ mb: 2.5 }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <BusinessIcon
                                            sx={{
                                                color: '#1652f0',
                                                fontSize: '1.25rem',
                                                mr: 1.5,
                                            }}
                                        />
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: '#0f172a',
                                                    fontWeight: 600,
                                                    fontSize: '1rem',
                                                }}
                                            >
                                                Company Assignment
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#64748b', fontSize: '0.8rem' }}
                                            >
                                                {isInternal
                                                    ? 'Internal company'
                                                    : 'External company'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {(canMoveCompany || canEditCompanyDetails) && (
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                            {canMoveCompany && (
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<MoveIcon />}
                                                    onClick={() => {
                                                        setError('');
                                                        setSuccess('');
                                                        setMoveCompanyOpen(true);
                                                    }}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    Move Company
                                                </Button>
                                            )}
                                            {canEditCompanyDetails && (
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<EditIcon />}
                                                    onClick={() => {
                                                        setError('');
                                                        setSuccess('');
                                                        setEditBusinessOpen(true);
                                                    }}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    Edit Company Details
                                                </Button>
                                            )}
                                        </Stack>
                                    )}
                                </Stack>

                                <Grid2 container spacing={3}>
                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                        {renderLabel('Company Name', userData.businessName)}
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                        {renderLabel(
                                            'Company Phone',
                                            formatPhoneNumber(userData.businessPhone)
                                        )}
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                        {renderLabel('Company Email', userData.businessEmail)}
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                        {renderLabel(
                                            'Company Address',
                                            userData.businessAddress || 'Not provided'
                                        )}
                                    </Grid2>
                                </Grid2>
                            </Box>
                        )}

                        <Box sx={SECTION_SX}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                                <AccountIcon
                                    sx={{ color: '#1652f0', fontSize: '1.25rem', mr: 1.5 }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{ color: '#0f172a', fontWeight: 600, fontSize: '1rem' }}
                                >
                                    Account Information
                                </Typography>
                            </Box>
                            <Grid2 container spacing={3}>
                                <Grid2 size={{ xs: 12, sm: 4 }}>
                                    {renderLabel(
                                        'User Type',
                                        isInternal ? 'Internal User' : 'External User'
                                    )}
                                </Grid2>
                            </Grid2>
                        </Box>
                    </Stack>
                </DialogContent>

                {isEditing && (
                    <DialogActions
                        sx={{
                            px: { xs: 2, sm: 3 },
                            py: { xs: 2, sm: 2.5 },
                            borderTop: '1px solid #f1f5f9',
                            gap: 1,
                        }}
                    >
                        <Button
                            onClick={handleCancel}
                            variant="outlined"
                            disabled={loading}
                            startIcon={<CancelIcon />}
                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 500 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveUser}
                            variant="contained"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 500,
                                backgroundColor: '#1652f0',
                                '&:hover': { backgroundColor: '#1348d1' },
                            }}
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            <MoveCompanyDialog
                open={moveCompanyOpen}
                onClose={() => setMoveCompanyOpen(false)}
                user={userData}
                onMoveComplete={handleMoveComplete}
            />

            <EditBusinessDialog
                open={editBusinessOpen}
                onClose={() => setEditBusinessOpen(false)}
                user={userData}
                onSaveComplete={handleEditBusinessComplete}
            />
        </>
    );
};

export default UserDetailsModal;
